// ============================================================
// DB-Bootstrap — safeOpenDB mit Pre-Migration-Backup + Audit-Log
// Spezifikation: Prompt v6.7 Abschnitt E (safeOpenDB)
// ============================================================

import { db } from './schema';
import { SessionKeyStore } from '../services/keyStore';
import { encrypt, uint8ToBase64 } from '../services/crypto';
import { seedMandantenIfEmpty } from '../services/mandantenService';
import { registerSyncMiddleware } from '../services/syncService';

const CURRENT_SCHEMA_VERSION = 8;

// ── Pre-Migration-Backup ───────────────────────────────────────────────────

async function isVersionUpgradePending(): Promise<boolean> {
  try {
    const existing = await new Promise<number>((resolve, reject) => {
      const req = indexedDB.open('AmedDB');
      req.onsuccess = () => {
        const version = req.result.version;
        req.result.close();
        resolve(version);
      };
      req.onerror = () => reject(req.error);
      // onupgradeneeded = DB existiert noch nicht → kein Upgrade nötig
      req.onupgradeneeded = () => {
        req.result.close();
        resolve(CURRENT_SCHEMA_VERSION); // Neu-Anlage, kein Upgrade
      };
    });
    return existing < CURRENT_SCHEMA_VERSION;
  } catch {
    return false;
  }
}

export async function createEncryptedBackup(label: string): Promise<void> {
  const key = SessionKeyStore.get();
  if (!key) {
    // Kein aktiver Key → Klartext-Backup als JSON (nur Struktur, keine Patientendaten)
    console.warn('[Bootstrap] Kein Session-Key für verschlüsselten Backup — überspringe.');
    return;
  }

  try {
    const tables = [
      'mandanten', 'mitarbeiter', 'arbeitsplaetze', 'vorsorge',
      'honorarvereinbarungen', 'leistungserfassung', 'rechnungen',
    ] as const;

    const exportData: Record<string, unknown[]> = {};
    for (const name of tables) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      exportData[name] = await (db as any)[name].toArray();
    }

    const json       = JSON.stringify({ label, ts: new Date().toISOString(), data: exportData });
    const plainBytes = new TextEncoder().encode(json);
    const encrypted  = await encrypt(plainBytes, key);
    const b64        = uint8ToBase64(encrypted);

    // Backup in separatem IDB-Store (außerhalb Dexie-Versionierung)
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.open('AmedBackups', 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore('backups', { keyPath: 'label' });
      };
      req.onsuccess = () => {
        const bdb   = req.result;
        const tx    = bdb.transaction('backups', 'readwrite');
        const store = tx.objectStore('backups');
        store.put({ label, ts: new Date().toISOString(), enc: b64 });
        tx.oncomplete = () => { bdb.close(); resolve(); };
        tx.onerror    = () => { bdb.close(); reject(tx.error); };
      };
      req.onerror = () => reject(req.error);
    });

    console.info(`[Bootstrap] Backup erstellt: ${label}`);
  } catch (err) {
    console.error('[Bootstrap] Backup fehlgeschlagen:', err);
    // Backup-Fehler → NICHT blockieren, aber loggen
  }
}

// ── Schema-Integritäts-Prüfung ─────────────────────────────────────────────

async function validateSchemaIntegrity(): Promise<void> {
  const requiredStores = [
    'cryptoMeta', 'mandanten', 'mitarbeiter', 'arbeitsplaetze',
    'vorsorge', 'audit_log', 'calendar_sync', 'honorarvereinbarungen',
    'leistungserfassung', 'learn_patterns', 'rechnungen', 'mahnungen',
    'einstellungen', 'zeiteintraege',
  ];
  for (const name of requiredStores) {
    if (!db.tables.find(t => t.name === name)) {
      throw new Error(`Schema-Integritätsfehler: Store "${name}" fehlt nach Migration!`);
    }
  }
}

// ── Audit-Log-Hilfsfunktion ────────────────────────────────────────────────

export async function logAudit(
  aktion: string,
  details?: Record<string, unknown>,
): Promise<void> {
  try {
    await db.audit_log.add({
      schema_version: 1,
      timestamp:  new Date().toISOString(),
      firma:      'SYSTEM',
      aktion,
      ...(details !== undefined ? { details: JSON.stringify(details) } : {}),
      user_agent: navigator.userAgent.slice(0, 120),
    });
  } catch (err) {
    // Audit-Log-Fehler sind kritisch → nach oben werfen
    throw new Error(`Audit-Log-Schreibfehler: ${String(err)}`);
  }
}

// ── Haupt-Bootstrap ────────────────────────────────────────────────────────

let dbReady = false;

export async function safeOpenDB(): Promise<void> {
  if (dbReady) return;

  try {
    // 1. Pre-Migration-Backup wenn Schema-Sprung erwartet
    if (await isVersionUpgradePending()) {
      await createEncryptedBackup(`pre-migration-v${CURRENT_SCHEMA_VERSION}-${new Date().toISOString()}`);
    }

    // 2. Sync-Middleware VOR dem Öffnen registrieren (dirty-Tracking)
    registerSyncMiddleware();

    //    DB öffnen → triggert Dexie-Migrations
    await db.open();

    // 3. Post-Migration-Validierung
    await validateSchemaIntegrity();

    // 4. Seed-Daten (Mandanten) beim ersten Start
    await seedMandantenIfEmpty();

    // 5. Audit-Log-Eintrag
    await logAudit('DB_OPENED', { version: db.verno });

    dbReady = true;
    console.info(`[Bootstrap] AmedDB v${db.verno} bereit.`);
  } catch (err) {
    // Hard-Block — wird von Error-Boundary (Schritt 1d) abgefangen
    window.dispatchEvent(new CustomEvent('amed:db-critical-error', { detail: err }));
    throw err;
  }
}

export function isDbReady(): boolean {
  return dbReady;
}
